import { useEffect, useState } from 'react';
import { ContentLayout } from 'components/Layout';
import { useAuth } from 'lib/auth';
import { useStatistics } from '../api/getStatistics';
import { Button, Text } from 'components/Elements';
import { useNavigate } from 'react-router-dom';
import { NewCustomers } from '../components/NewCustomers';
import { KYCSubmissions } from '../components/KYCSubmissions';
import { ApprovalRequired } from '../components/ApprovalRequired';
import { useDevSettings } from 'hooks/useDevSettings';
import { RecentTransactions } from '../components/RecentTransactions';
import { ChevronRight, Clock7, AlertTriangle, Activity } from 'lucide-react';
import { NotificationsConsent } from 'components/Notifications';
import { useStoreFCMToken } from 'features/notifications';
import { getDynamicGreeting } from 'utils/greetings';
import { LiveLogFeed } from '../components/LiveLogFeed';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { InteractiveThreatMap } from '../components/InteractiveThreatMap';
import { SystemHealth } from '../components/SystemHealth';

// Mock function to simulate dynamic updates
const generateSystemMetrics = () => [
  {
    metric: 'API Latency',
    value: `${Math.floor(100 + Math.random() * 150)}ms`,
    status: 'normal',
  },
  {
    metric: 'Error Rate',
    value: `${(Math.random() * 0.5).toFixed(2)}%`,
    status: Math.random() > 0.95 ? 'alert' : 'normal',
  },
  {
    metric: 'Active Users',
    value: `${(3000 + Math.floor(Math.random() * 3000)).toLocaleString()}`,
    status: 'high',
  },
  {
    metric: 'Suspicious Logins',
    value: `${Math.floor(Math.random() * 5)} (24h)`,
    status: Math.random() > 0.7 ? 'alert' : 'normal',
  },
];

// Mock threat map events
const generateThreatEvents = () => [
  {
    id: 1,
    lat: 40.7128,
    lng: -74.006,
    country: 'USA',
    type: 'Suspicious Login',
    ip: '192.168.12.45',
  },
  {
    id: 2,
    lat: 51.5074,
    lng: -0.1278,
    country: 'UK',
    type: 'Successful Login',
    ip: '172.16.4.32',
  },
  {
    id: 3,
    lat: 35.6762,
    lng: 139.6503,
    country: 'Japan',
    type: 'Failed MFA',
    ip: '10.45.32.12',
  },
  {
    id: 4,
    lat: -33.8688,
    lng: 151.2093,
    country: 'Australia',
    type: 'Suspicious API Key',
    ip: '203.45.11.87',
  },
];

export const Dashboard = () => {
  const { user } = useAuth();
  const statsQuery = useStatistics();
  const { userStatus } = useDevSettings();
  const navigate = useNavigate();
  const storeFCMTokenMutation = useStoreFCMToken();

  const [systemMetrics, setSystemMetrics] = useState(generateSystemMetrics());
  const [threatEvents, setThreatEvents] = useState(generateThreatEvents());

  const loading = statsQuery.isLoading;
  const stats = statsQuery.data;
  const greeting = getDynamicGreeting(user.firstName);

  // Simulate live updates (polling every 5s)
  useEffect(() => {
    const interval = setInterval(() => {
      setSystemMetrics(generateSystemMetrics());
      setThreatEvents(generateThreatEvents());
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleToken = async (token) => {
    await storeFCMTokenMutation.mutateAsync({ data: { fcmToken: token } });
  };

  return (
    <ContentLayout nobg title="Overview" subTitle={greeting}>
      <div className="relative pb-12">
        <NotificationsConsent onTokenReceived={handleToken} />
        <div
          className={
            userStatus !== 'active' ? 'blur-md transition' : 'transition'
          }
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Section */}
            <div className="col-span-2 space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {loading ? (
                  [...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="animate-pulse h-36 rounded-3xl bg-white/10 border border-white/20 backdrop-blur-xl"
                    />
                  ))
                ) : (
                  <>
                    <NewCustomers
                      clientsAddedToday={stats?.clientsAddedToday}
                    />
                    <KYCSubmissions
                      pendingKYCSubmissions={stats?.pendingKYCSubmissions}
                    />
                    <ApprovalRequired
                      unapprovedWithdraws={stats?.unapprovedWithdraws}
                    />
                  </>
                )}
              </div>

              {/* Transactions Graph */}
              <div className="p-4 rounded-3xl bg-white/10 backdrop-blur-xl border border-white/20 shadow-lg">
                <Text variant="body" className="text-white font-semibold mb-2">
                  Transaction Volume (Last 24h)
                </Text>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart
                    data={[
                      { time: '00:00', volume: 120 },
                      { time: '06:00', volume: 400 },
                      { time: '12:00', volume: 700 },
                      { time: '18:00', volume: 500 },
                      { time: '24:00', volume: 300 },
                    ]}
                  >
                    <XAxis dataKey="time" stroke="#bbb" />
                    <YAxis stroke="#bbb" />
                    <Tooltip
                      contentStyle={{
                        background: '#1E293B',
                        borderRadius: '8px',
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="volume"
                      stroke="#4ADE80"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Recent Transactions */}
              <div className="relative z-10 bg-white/10 p-3 rounded-3xl border border-white/20">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex flex-row items-center space-x-2">
                    <Clock7 size={20} />
                    <Text variant="body" className="text-white">
                      Recent Transactions
                    </Text>
                  </div>
                  {stats?.recentTransactions?.length > 0 && (
                    <button
                      onClick={() => navigate('/app/transactions')}
                      className="px-3 py-1 rounded-lg flex flex-row items-center space-x-2 bg-white/10 hover:bg-white/20 text-sm font-medium shadow"
                    >
                      <span>See All</span>
                      <ChevronRight size={12} />
                    </button>
                  )}
                </div>
                {loading ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="h-12 bg-white/20 rounded-lg animate-pulse"
                      />
                    ))}
                  </div>
                ) : (
                  <RecentTransactions
                    transactions={stats?.recentTransactions}
                  />
                )}
              </div>
            </div>

            {/* Right Section */}
            <div className="space-y-6">
              {/* Live System Health */}
              <SystemHealth systemMetrics={systemMetrics} />

              {/* Threat Map */}
              <div className="rounded-3xl backdrop-blur-xl bg-white/10 border border-white/20 shadow-lg">
                <div className="px-6 pt-6">
                  <Text
                    variant="body"
                    className="text-white font-semibold mb-4"
                  >
                    Threat Map (Live Logins)
                  </Text>
                </div>
                <InteractiveThreatMap />
              </div>

              {/* Live Security Feed */}
              <div className="relative p-6 rounded-3xl backdrop-blur-xl bg-white/10 border border-white/20 shadow-lg">
                <LiveLogFeed />
              </div>
            </div>
          </div>
        </div>

        {/* Inactive Account Overlay */}
        {userStatus !== 'active' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/80 text-white z-50 rounded-3xl">
            <h2 className="text-3xl font-bold mb-2">Account Inactive</h2>
            <p className="text-center max-w-md px-4 mb-4">
              Your account is currently inactive and has limited functionality.
              Please contact your administrator to request activation.
            </p>
            <Button>Submit Request</Button>
          </div>
        )}
      </div>
    </ContentLayout>
  );
};
